import { ICustomBinding } from '#/DefaultViewExtensionInterfaces';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ResourceManager from '@/Management/ResourceManager';
import Factory from '@/Factories/Factory';
import ControlFactory from '@/Factories/ControlFactory';
import FilterElementViewModel from '@/ViewModels/Controls/FilterElementViewModel';
import GenericObject from '@/Model/GenericObject';
import { IDVGItemDTO } from "coreframetypings/dto";
import LocatorHelper from '@/Utility/LocatorHelper';
import Utilities from '@/Utility/Utilities';
import GlobalConfiguration from '@/Management/GlobalConfiguration';

export default class FilterElementBinding implements ICustomBinding {

    public initBinding() {
        ko.bindingHandlers.filterElement = {
            init: (element: HTMLElement, valueAccessor: () => any) => {
                const filterElement = $(element);
                const control = filterElement.find('.control');
                const options = valueAccessor();
                const columns = options.columns;
                const viewModel: FilterElementViewModel = options.context;

                const subscription = columns.value.subscribe(() => {
                    FilterElementBinding.onChange(false, control, viewModel, columns.selected(), options);
                });
                ko.utils.domNodeDisposal.addDisposeCallback(filterElement[0], () => {
                    subscription.dispose();
                });
                FilterElementBinding.onChange(true, control, viewModel, columns.selected(), options);
            }
        };
    }

    /**
     * Wird aufgerufen, wenn sich die ausgewähle Spalte ändert
     * @param initalizing Gibt an, ob die Zeile gerade initialisiert wird
     * @param control Das DOM-Element, welches die Zeile wiederspiegelt
     * @param viewModel Das zugehörige ViewModel für die Zeile
     * @param column Das Objekt zur Spalte
     * @param options Optionen für das Binding
     */
    private static onChange(initalizing: boolean, control: JQuery<HTMLElement>, viewModel: FilterElementViewModel, column: any, options: FilterElementBindingOptions) {
        control.empty();

        if (column != null) {
            if (column.listViewPDOID > 0) {
                // Assoziationen
                RADEnvironmentProxy.getInstance().getListViewForFilter({
                    listViewPDO_ID: column.listViewPDOID
                }).then((listView) => {
                    listView.showButtonCreate = false;
                    listView.showButtonEdit = false;
                    listView.showButtonDisconnect = false;
                    listView.showButtonDelete = false;
                    listView.showMoveButtons = false;
                    listView.showButtonCSVExport = false;
                    listView.showButtonXLSExport = false;
                    listView.showButtonPDFExport = false;
                    listView.showButtonSelect = true;
                    listView.staticFilterExpression = null;

                    const detailViewItem: IDVGItemDTO = $.extend({}, column);
                    detailViewItem.name = column.name.replace('.ObjectIdentValue', '');
                    detailViewItem.dotNetTypeName = column.typeName;
                    detailViewItem.listView = listView;
                    detailViewItem.useInFilter = true;

                    // SAGGRC-548: Globale Listen können den Scope der Relation vergrößern,
                    // ABER: der Scope einer globalen Relation kann niemals verkleinert werden
                    if (options.includeAllPrincipals)
                        detailViewItem.listView.includeAllPrincipals = options.includeAllPrincipals;

                    if (options.includeBasePrincipals !== undefined)
                        detailViewItem.listView.includeBasePrincipals = options.includeBasePrincipals;

                    if (options.includeSubPrincipals !== undefined)
                        detailViewItem.listView.includeSubPrincipals = options.includeSubPrincipals;

                    FilterElementBinding.generateFactory(initalizing, control, viewModel, column.uiGenerator, column.typeName, {
                        detailViewItem: detailViewItem
                    });
                });
            } else if (column.possiblePDEnumerationValues != null && _.keys(column.possiblePDEnumerationValues).length > 0) {
                // Enumerations
                const detailViewItem: IDVGItemDTO = $.extend({}, column);
                detailViewItem.name = column.name.replace('.MLSValue', '');
                detailViewItem.dotNetTypeName = column.typeName;
                detailViewItem.isPDEnumeration = true;
                detailViewItem.useInFilter = true;

                FilterElementBinding.generateFactory(initalizing, control, viewModel, column.uiGenerator, column.typeName, {
                    detailViewItem: detailViewItem
                });
            } else {
                //Sonstiges: DateTime; String....
                const detailViewItem: IDVGItemDTO = $.extend({}, column);
                detailViewItem.dotNetTypeName = column.typeName;
                detailViewItem.useInFilter = true;

                FilterElementBinding.generateFactory(initalizing, control, viewModel, column.uiGenerator, column.typeName, {
                    detailViewItem: detailViewItem
                });
            }
        } else {
            FilterElementBinding.generateFactory(initalizing, control, viewModel, 'TextBox', null, {
                detailViewItem: { name: 'value', useInFilter: true }
            });
        }
    }

    /**
     * Erzeugt die entsprechende ControlFactory für ein FilterElement
     * @param initalizing Gibt an, ob die Zeile gerade initialisiert wird
     * @param control Das DOM-Element, welches die Zeile wiederspiegelt
     * @param viewModel Das zugehörige ViewModel für die Zeile
     * @param uiGenerator Der UIGenerator-String für das Filter-Control
     * @param typeName TODO, Parameter-Zweck nicht bekannt
     * @param factoryOptions Optionen, die an die Factory durchgereicht werden
     */
    private static generateFactory(initalizing: boolean, control: JQuery<HTMLElement>, viewModel: FilterElementViewModel, uiGenerator: string, typeName: string, factoryOptions: any) {
        const oldFactory = viewModel.filterFactory();
        // Wenn das Binding initialisiert wird, sollen keine Werte zurückgesetzt werden
        // Soll nur passieren, wenn sich der Wert des DropDowns für die "Spalte" ändert
        if (!Utilities.isNullOrUndefined(oldFactory) && !initalizing) {
            oldFactory.resetValue();
            oldFactory.dispose();
            viewModel.filterFactory(null);
        }

        if (viewModel.filter != null) {
            factoryOptions.onSubmit = viewModel.filter.onSubmit;
        }
        factoryOptions.context = viewModel;
        factoryOptions.locator = LocatorHelper.buildLocator(viewModel.locator, 'control');
        
        let controlFactory = Factory.createInstance(uiGenerator, factoryOptions) as ControlFactory;
        const filterInfo = controlFactory.getFilterInfo();
        
        // Soll ein anderes Filter-Control verwendet werden?
        if (filterInfo.uiGeneratorReplacement){
            controlFactory?.dispose();
            controlFactory = Factory.createInstance(filterInfo.uiGeneratorReplacement, factoryOptions) as ControlFactory;
        }
        
        const tuple = controlFactory.getTuple();

        control.append(tuple.control);

        if (typeName != null) {
            const operators = controlFactory.getAvailableOperators().map((op: Operator) => {
                op.name = ResourceManager.getInstance().get(op.ergName);
                op.locator = op.value;
                return op;
            });

            GlobalConfiguration.listView.filterConfiguration.filterOperatorSortingStrategy(operators);

            controlFactory.isEnabled(true);

            viewModel.dropDownListOperators.data(operators);
            if (!viewModel.operator()) {
                viewModel.dropDownListOperators.value(operators[0].ergName);
            }
            viewModel.dropDownListOperators.isEnabled(true);
        } else {
            controlFactory.isEnabled(false);

            viewModel.dropDownListOperators.data([]);
            viewModel.dropDownListOperators.value(null);
            viewModel.dropDownListOperators.isEnabled(false);
        }

        viewModel.filterFactory(controlFactory);
    }
}

interface Operator {
    name: string;
    locator: string;
    value: string;
    ergName: string;
}

interface FilterElementBindingOptions {
    genericObject: GenericObject;
    includeAllPrincipals?: boolean;
    includeBasePrincipals?: boolean;
    includeSubPrincipals?: boolean;
}